import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BootstrapView } from '@ulp/consumer-core';
import { apiFetch, ApiError, configureClient } from './api';
import { cacheRead, cacheWrite, writeJson } from './storage';
import { isPhase1 } from './phase1-rules';

export interface TenantContextValue {
  slug: string;
  bootstrap: BootstrapView | null;
  /** Açılış bilgisi yükleniyor (ilk kez, önbellek yok). */
  loading: boolean;
  /** İşletme bulunamadı (yanlış bağlantı). */
  notFound: boolean;
  /** Yol üretici: `/${slug}/...` */
  path(p?: string): string;
}

const Ctx = createContext<TenantContextValue | null>(null);

const HEX = /^#([0-9a-f]{6})$/i;

function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/**
 * İşletme rengini temaya uygular. Beyaz metnin okunabilirliği için yalnız yeterince koyu renkler (<0.3 bağıl
 * parlaklık) ana renk olur; açık renkler yok sayılır (zümrüt varsayılan kalır). Koyu temada renk açılır.
 */
function applyBrand(color: string | null | undefined): void {
  const id = 'tenant-brand';
  document.getElementById(id)?.remove();
  if (!color || !HEX.test(color) || luminance(color) > 0.3) return;
  const style = document.createElement('style');
  style.id = id;
  const light = `--brand:${color};--brand-deep:color-mix(in srgb,${color} 58%,#000);--brand-night:color-mix(in srgb,${color} 34%,#000);--brand-soft:color-mix(in srgb,${color} 12%,#fff);`;
  const dark = `--brand:color-mix(in srgb,${color} 62%,#fff);--brand-deep:color-mix(in srgb,${color} 50%,#000);--brand-night:color-mix(in srgb,${color} 26%,#000);--brand-soft:color-mix(in srgb,${color} 22%,#06110d);`;
  style.textContent = `:root{${light}}@media (prefers-color-scheme: dark){:root:not([data-theme='light']){${dark}}}:root[data-theme='dark']{${dark}}`;
  document.head.appendChild(style);
}

export function TenantProvider({ slug, children }: { slug: string; children: ReactNode }) {
  // İstemciye işletmeyi hemen bildir (ilk istekten önce).
  configureClient({ slug });

  const query = useQuery({
    queryKey: ['bootstrap', slug],
    queryFn: async () => {
      try {
        const data = await apiFetch<BootstrapView>('/app/bootstrap', { anonymous: true });
        cacheWrite(slug, 'bootstrap', data);
        return data;
      } catch (e) {
        // Çevrimdışı: son kopya ile aç.
        const cached = cacheRead<BootstrapView>(slug, 'bootstrap');
        if (cached && e instanceof ApiError && e.isNetwork) return cached.data;
        throw e;
      }
    },
    staleTime: 10 * 60_000,
    retry: (count, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && count < 2,
    initialData: () => cacheRead<BootstrapView>(slug, 'bootstrap')?.data,
    initialDataUpdatedAt: () => cacheRead<BootstrapView>(slug, 'bootstrap')?.savedAt,
  });

  const bootstrap = query.data ?? null;

  useEffect(() => {
    writeJson(null, 'lastTenant', slug);
  }, [slug]);

  useEffect(() => {
    applyBrand(bootstrap?.brand?.primaryColor);
    if (bootstrap?.tenant.name) document.title = bootstrap.tenant.name;
  }, [bootstrap?.brand?.primaryColor, bootstrap?.tenant.name]);

  // Phase 1 işletmesi: "Ana ekrana ekle" işletme adı/rengiyle ve /{slug}/ kapsamıyla kurulsun. Eski işletmeler genel
  // Super APP manifest'inde kalır.
  const phase1 = isPhase1(bootstrap);
  useEffect(() => {
    if (!phase1) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;
    const prev = link.getAttribute('href');
    link.setAttribute('href', `/api/app/manifest.webmanifest?tenantSlug=${encodeURIComponent(slug)}`);
    return () => {
      if (prev) link.setAttribute('href', prev);
    };
  }, [phase1, slug]);

  const notFound = query.error instanceof ApiError && (query.error.code === 'TENANT_NOT_FOUND' || query.error.status === 400);

  const value = useMemo<TenantContextValue>(
    () => ({
      slug,
      bootstrap,
      loading: query.isLoading,
      notFound,
      path: (p = '') => `/${slug}${p ? (p.startsWith('/') ? p : `/${p}`) : ''}`,
    }),
    [slug, bootstrap, query.isLoading, notFound],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTenant TenantProvider içinde kullanılmalı');
  return ctx;
}
